import React, {Fragment, ReactElement, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardMedia,
    CircularProgress,
    Pagination,
    Toolbar,
    Typography
} from "@mui/material";
import SnackError from "../../components/SnackError";
import {
    PersonApiGet,
    PersonFilter,
    useDeletePersonByIdMutation,
    useGetPersonQuery,
    usePutPersonByIdMutation,
} from "./people_api";
import Cancel from "@mui/icons-material/Cancel";
import Delete from "@mui/icons-material/Delete";
import DoNotDisturb from "@mui/icons-material/DoNotDisturb";
import Edit from "@mui/icons-material/Edit";
import Upload from "@mui/icons-material/Upload";
import {SearchBox} from "../../components/SearchBox";
import sortComparator from "../../libs/sortComparator";
import filterFunction from "../../libs/filterFunction";
import pager from "../../libs/pager";
import {Route, Routes, useHref} from "react-router-dom";
import {PersonCreate} from "./PersonCreate";
import {PersonEdit} from "./PersonEdit";
import SortBox from "../../components/SortBox";
import Title from "../navigation/Title";
import usePaginator from "../../hooks/usePaginator";
import userPlaceholder from "/assets/imgs/user.jpg"
import {ListCardDataCol, ListGrid} from "../../components/styled";
import useDownloadImage from "../storage/useDownloadImage";
import ImageUploadDialog from "../storage/ImageUploadDialog";

export type PersonItemProps = {
    onDelete?: (id: number) => void,
    item: PersonApiGet
    selectable?: boolean;
    onSelect?: (id: number) => void;

}

const PersonItem = (props: PersonItemProps) => {
    const {item, selectable, onSelect, onDelete} = props;

    const [put] = usePutPersonByIdMutation();
    const [uploading, setUploading] = useState(false)

    const onUpload = (path: string) => {
        put({body: {avatar_url: path}, id: item.id})
        setUploading(false)
    }

    const path = useHref(`./${item.id}/edit`);
    const avatarUrl = useDownloadImage("avatars", item.avatar_url)

    if (!item) {
        return <Box>
            <Alert severity={"error"}>Object not found!</Alert>
        </Box>
    }

    return <Card onClick={() => {
        selectable && onSelect && onSelect(item.id);
    }} sx={{
        display: "flex"
    }}>
        <CardMedia
            component="img"
            sx={{width: 120, height: 120, minWidth: 120, objectFit: "cover",}}
            image={avatarUrl || userPlaceholder}
            alt={item.lastName + " " + item.firstName}
        />
        <ListCardDataCol>
            <CardContent sx={{width: "100%"}}>
                <Typography variant={"h6"}>{item.lastName} {item.firstName}</Typography>
                <Typography variant={"body1"}>{item.partnerCode}</Typography>

            </CardContent>
            {!selectable && <CardActions>
                <Button onClick={() => setUploading(true)}
                        title={"Naloži sliko"}
                ><Upload fontSize={"small"}/></Button>
                <Button
                    title={item.Active ? "Onemogoči" : "Omogoči"}
                    sx={{
                        color: item.Active ? undefined : "error.main"
                    }} onClick={() => {
                    put({body: {Active: !item.Active}, id: item.id})
                }}>{!item.Active ? <Cancel/> : <DoNotDisturb/>}</Button>
                <Button href={path}
                        title={"Uredi osebo"}
                ><Edit fontSize={"small"}/></Button>
                <Button title="Briši osebo"
                        onClick={() => {
                            onDelete && onDelete(item.id);
                        }}
                ><Delete fontSize={"small"}/> </Button>
            </CardActions>}
        </ListCardDataCol>

        <ImageUploadDialog
            uploading={uploading}
            onUpload={onUpload}
            bucket={"avatars"}
            onClose={() => setUploading(false)}
        />
    </Card>
}


export type PersonListProps = {
    list: PersonApiGet[],
    selectable?: boolean;
    onSelect?: (id: number) => void;
}

export const PersonList = (props: PersonListProps) => {
    const {list, onSelect, selectable} = props;

    const [deletePerson, {
        error: deletePersonError
    }] = useDeletePersonByIdMutation();

    const handleDelete = (id: number) => {
        deletePerson({id});
    }

    return <ListGrid>
        {deletePersonError && <SnackError error={deletePersonError}/>}
        {list.map(param => <PersonItem key={param.id}
                                       item={param}
                                       onSelect={onSelect}
                                       selectable={selectable}
                                       onDelete={handleDelete}/>
        )}
    </ListGrid>
}

type PersonControllerProps = {
    pageSize?: number;
    selectable?: boolean;
    onSelect?: (id: number) => void;
    customFilter?: ReactElement
}

const sortFields = {
    lastName: "Naziv",
    partnerCode: "Koda",
    status: "Aktiven",
}
export const PersonListController = function ({pageSize, selectable, onSelect}: PersonControllerProps) {

    const {
        filter,
        setFilter,
        page,
        setPage,
        sortField,
        setSortField,
        sortDirection,
        setSortDirection
    } = usePaginator("workers", sortFields,)

    const f: PersonFilter = {};
    if (selectable) {
        f.active = true;
    }
    const {data, error, isFetching} = useGetPersonQuery(f);

    const path = useHref("./create");
    if (error) {
        return <SnackError error={error}/>;
    }
    if (isFetching || !data) {
        return <CircularProgress/>
    }

    const list: Array<PersonApiGet> = data && data.filter(filterFunction(filter, ["firstName", "lastName", "partnerCode", "code", "status"]))
        .sort(sortComparator(sortDirection, sortField)) || [];
    const [pages, currentPage] = pager(list, page, pageSize);

    return <Fragment>
        <Toolbar sx={{
            display: "flex",
            "&.MuiToolbar-root": {p: 0},
            mb: 1,
            justifyContent: "space-between",
            alignItems: "flex-end",
            flexWrap: "wrap"
        }}>
            {!selectable && <Button href={path}>Ustvari osebo</Button>}
            <Box sx={{display: "flex"}}>
                <SearchBox value={filter} onChange={setFilter}/>
                <SortBox sortDirection={sortDirection} sortField={sortField}
                         onChangeDirection={setSortDirection}
                         onSelectField={setSortField} fields={sortFields}
                />
            </Box>
        </Toolbar>
        {error && <SnackError error={error}/>}

        <PersonList list={currentPage} selectable={selectable} onSelect={onSelect}/>
        <Pagination page={page} onChange={(e, page) => {
            setPage(page)
            console.log("onchange")
        }} count={pages}/>
    </Fragment>

}
export type PersonProps = {
    pageSize?: number;
}

export default ({pageSize = 15}: PersonProps) => {

    return <Routes>
        <Route path={"/create"} element={<PersonCreate/>}/>
        <Route path={"/:id/edit"} element={<PersonEdit/>}/>
        <Route path={"/"} element={<Fragment>
            <Title title={"Upravljanje oseb"}/>
            <PersonListController pageSize={pageSize}/>
        </Fragment>}/>
    </Routes>
}
